import React, { useState, useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { waysToWear } from "@/lib/categoryAssets";
import { useReduceMotion } from "@/lib/motion";

// "Ways To Wear It" — ONE recommended colour, MANY realistic garment forms.
// Calm 5s editorial crossfade (colour constant, silhouette + label change together).
// Pauses on hover / touch; auto-advance resets on manual navigation.
// The in-app "reduce motion" toggle holds it still (no auto-play, no fade).
const INTERVAL = 5000;

export const WaysToWear = ({ rec }) => {
  const items = waysToWear(rec);
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const reduced = useReduceMotion();
  const startX = useRef(0);

  useEffect(() => { setI(0); }, [rec?.color_hex]);

  // auto-advance — timer resets whenever `i` changes (manual nav included).
  // Held still when the visitor has chosen reduced motion.
  useEffect(() => {
    if (reduced || paused || items.length <= 1) return;
    const t = setTimeout(() => setI((p) => (p + 1) % items.length), INTERVAL);
    return () => clearTimeout(t);
  }, [i, paused, reduced, items.length]);

  if (!items.length) return null;

  const go = (n) => setI((n + items.length) % items.length);
  const active = items[i];
  const fade = reduced ? 0 : 1.1;

  const onStart = (e) => {
    startX.current = e.touches ? e.touches[0].clientX : e.clientX;
    setPaused(true);
  };
  const onEnd = (e) => {
    const endX = e.changedTouches ? e.changedTouches[0].clientX : e.clientX;
    const dx = endX - startX.current;
    if (Math.abs(dx) > 40) go(dx < 0 ? i + 1 : i - 1);
    setPaused(false);
  };

  return (
    <div className="mt-6" data-testid="ways-to-wear">
      <p className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170] mb-3">Ways to wear {rec.color_name}</p>
      <div
        className="relative border border-[#F5F2EA]/10 bg-[#F5F2EA] overflow-hidden"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onTouchStart={onStart}
        onTouchEnd={onEnd}
      >
        <div className="relative aspect-[4/5] w-full">
          <AnimatePresence initial={false}>
            <motion.img
              key={active.src}
              src={active.src}
              alt={active.alt}
              loading="lazy"
              draggable="false"
              data-testid="ways-image"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: fade, ease: [0.22, 1, 0.36, 1] }}
              className="absolute inset-0 w-full h-full object-cover select-none"
            />
          </AnimatePresence>
        </div>

        {/* garment label — synced to the active garment */}
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0D0D0D]/85 to-transparent px-4 pt-10 pb-3 flex items-end justify-between pointer-events-none">
          <div className="min-w-0">
            <AnimatePresence mode="wait">
              <motion.p
                key={active.label}
                initial={{ opacity: 0, y: reduced ? 0 : 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: reduced ? 0 : -6 }}
                transition={{ duration: reduced ? 0 : 0.45, ease: "easeOut" }}
                className="font-serif text-lg text-[#F5F2EA] leading-tight truncate"
                data-testid="ways-label"
              >
                {active.label}
              </motion.p>
            </AnimatePresence>
            <p className="font-sans text-[9px] tracking-luxe uppercase text-[#C8A97E]">{rec.color_name}</p>
          </div>
          <span className="h-4 w-4 rounded-full border border-white/70 shrink-0" style={{ backgroundColor: rec.color_hex }} title={rec.color_name} />
        </div>

        {items.length > 1 && (
          <>
            <button onClick={() => go(i - 1)} aria-label="Previous garment" data-testid="ways-prev"
              className="absolute left-2 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-[#0D0D0D]/55 text-[#F5F2EA] flex items-center justify-center hover:bg-[#0D0D0D]/80 transition-colors">
              <ChevronLeft size={18} /></button>
            <button onClick={() => go(i + 1)} aria-label="Next garment" data-testid="ways-next"
              className="absolute right-2 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-[#0D0D0D]/55 text-[#F5F2EA] flex items-center justify-center hover:bg-[#0D0D0D]/80 transition-colors">
              <ChevronRight size={18} /></button>
          </>
        )}
      </div>

      {/* pagination dots */}
      <div className="flex items-center justify-center gap-1.5 mt-3">
        {items.map((it, idx) => (
          <button key={it.key} onClick={() => go(idx)} aria-label={`Show ${it.label}`}
            data-testid={`ways-dot-${idx}`}
            className={`h-1.5 rounded-full transition-all ${idx === i ? "w-5 bg-[#C8A97E]" : "w-1.5 bg-[#F5F2EA]/25 hover:bg-[#F5F2EA]/50"}`} />
        ))}
      </div>
      <p className="font-sans text-[10px] text-[#8A8170] text-center mt-2">One colour, many ways — swipe to explore.</p>
    </div>
  );
};

export default WaysToWear;
